import { useEffect, useState } from 'react'
import { Button, Card, Col, Descriptions, Flex, Row, theme } from 'antd'

import { useVerificationRequestsWidget } from '../../atoms/duplicates/hooks'
import { VerificationCommentModal } from '../VerificationCommentModal'
import { path } from '../../../../libs'
import { formatMoney } from '../../../../libs/utils'
import { formatDate } from 'src/libs/formatDates'

type Props = {
  clientId: string
  selectedRequestId?: string | null
  onSelect?: (duplicate: { clientId: string; requestId: string } | null) => void
}

export const VerificationDuplicates = ({
  clientId,
  selectedRequestId,
  onSelect,
}: Props) => {
  const [commentRequestId, setCommentRequestId] = useState<string | null>(null)
  const { verificationRequest } = useVerificationRequestsWidget(clientId)
  const { token } = theme.useToken()

  useEffect(() => {
    if (
      verificationRequest &&
      verificationRequest.length > 0 &&
      !selectedRequestId
    ) {
      const first = verificationRequest[0]
      onSelect?.({ clientId: first.clientId, requestId: first.requestId })
    }
  }, [verificationRequest])

  if (!verificationRequest || verificationRequest.length === 0) return null

  return (
    <Flex wrap="nowrap" gap={8} style={{ overflowX: 'auto' }}>
      {verificationRequest.map((item) => {
        const isSelected = item.requestId === selectedRequestId

        return (
          <Card
            size="small"
            style={{
              minWidth: 450,
              maxWidth: 550,
              flexShrink: 0,
              background: isSelected ? token.colorFillAlter : undefined,
              cursor: 'pointer',
            }}
            key={item.requestId}
            onClick={() =>
              onSelect?.(
                isSelected
                  ? null
                  : { clientId: item.clientId, requestId: item.requestId },
              )
            }
            title={
              <>
                {formatDate(item.updatedAt)}{' '}
                <a
                  href={path.clients.details(item.clientId)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.clientId}
                </a>
              </>
            }
          >
            <Row gutter={24}>
              <Col span={12}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Verify">
                    <span
                      style={{ color: item.client.verified ? 'green' : 'red' }}
                    >
                      {item.client.verified ? 'Yes' : 'No'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Has deposit">
                    <span
                      style={{
                        color: item.client.hasDeposit ? 'green' : 'red',
                      }}
                    >
                      {item.client.hasDeposit ? 'Yes' : 'No'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Balance">
                    {(() => {
                      const raw =
                        item.totalBalanceUSD ?? item.client.totalBalanceUSD
                      const style =
                        typeof raw === 'number' && raw > 0
                          ? { color: token.colorSuccess, fontWeight: 700 }
                          : raw === 0
                          ? { color: token.colorTextTertiary }
                          : undefined
                      return (
                        <span style={style}>{formatMoney(raw, 'USD')}</span>
                      )
                    })()}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Full name">
                    {item.firstName} {item.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {item.client.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of birth">
                    {item.dateOfBirth}
                  </Descriptions.Item>
                  <Descriptions.Item label="Document number">
                    {item.documentNumber}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={(e) => {
                e.stopPropagation()
                setCommentRequestId(item.clientId)
              }}
            >
              Add comment
            </Button>
          </Card>
        )
      })}
      <VerificationCommentModal
        open={commentRequestId !== null}
        onClose={() => setCommentRequestId(null)}
        entityType="client"
        entityId={commentRequestId ?? ''}
      />
    </Flex>
  )
}
